import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Formik, FormikErrors } from "formik";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { getMinPasswordLength } from "../constants";
import Autocomplete from "@mui/material/Autocomplete";
import strings from "../../config/localization";
import {
  useCreateUserMutation,
  useGetOrganizationsQuery,
} from "../../services/appln";

interface FormData {
  fullName: string;
  email: string;
  userName: string;
  passWord: string;
  organizationId: string;
}

export default function NewUserPage1() {
  const navigate = useNavigate();
  const defaultTheme = createTheme();

  const [createUser, params] = useCreateUserMutation();

  const initialValues: FormData = {
    fullName: "",
    userName: "",
    passWord: "",
    email: "",
    organizationId: "",
  };

  const orgQueryResponse = useGetOrganizationsQuery("");
  const orgMap = new Map<string, string>();
  orgQueryResponse.data?.data.forEach((element: any) => {
    orgMap.set(element.name, element.id);
  });

  if (params.isUninitialized || params.isError) {
    // trigger not called yet, show form
    return (
      <div>
        <Formik
          initialValues={initialValues}
          validate={(values) => {
            const errors: FormikErrors<FormData> = {};
            if (!values.fullName) {
              errors.fullName = strings.nameMandatory;
            }
            if (!values.userName) {
              errors.userName = strings.usernameMandatory;
            }
            if (!values.passWord) {
              errors.passWord = strings.passwordMandatory;
            } else if (values.passWord.length < getMinPasswordLength()) {
              errors.passWord = strings.formatString(
                strings.shortPassword,
                getMinPasswordLength(),
              ) as string;
            }
            return errors;
          }}
          onSubmit={(values, actions) => {
            console.log({ values, actions });
            values.organizationId = orgMap.get(values.organizationId!)!;
            actions.setSubmitting(false);
            createUser(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            handleSubmit,
            setFieldValue,
          }) => (
            <ThemeProvider theme={defaultTheme}>
              <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                  sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                    <LockOutlinedIcon />
                  </Avatar>
                  <Typography component="h1" variant="h5">
                    {strings.createUser}
                  </Typography>
                  {params.isError ? (
                    <div>
                      <Alert severity="error">
                        <AlertTitle>
                          {strings.registrationFailedTitle}
                        </AlertTitle>
                        {strings.registrationFailedMsg}
                      </Alert>
                    </div>
                  ) : null}
                  <form onSubmit={handleSubmit}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="fullName"
                      name="fullName"
                      label={strings.name}
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.fullName && Boolean(errors.fullName)}
                      helperText={touched.fullName && errors.fullName}
                      autoFocus
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="userName"
                      name="userName"
                      label={strings.username}
                      value={values.userName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.userName && Boolean(errors.userName)}
                      helperText={touched.userName && errors.userName}
                    />
                    <TextField
                      fullWidth
                      required
                      margin="normal"
                      id="passWord"
                      name="passWord"
                      type="password"
                      label={strings.password}
                      value={values.passWord}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.passWord && Boolean(errors.passWord)}
                      helperText={touched.passWord && errors.passWord}
                    />
                    <Autocomplete
                      fullWidth
                      disablePortal
                      id="organizationId"
                      options={Array.from(orgMap.keys())}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label={strings.organization}
                        />
                      )}
                      onChange={(e, value: any) => {
                        console.log(value);
                        setFieldValue("organizationId", value);
                      }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      sx={{ mt: 3, mb: 2 }}
                    >
                      {strings.next}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(-1);
                      }}
                    >
                      {strings.cancel}
                    </Button>
                  </form>
                </Box>
              </Container>
            </ThemeProvider>
          )}
        </Formik>
      </div>
    );
  }

  if (!params.isUninitialized && params.isLoading) {
    // application is being created. show progress indicator
    // backdrop is shown over empty screen...
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!params.isUninitialized && params.isSuccess) {
    navigate("/../newUser2/" + params.data.id);
    return <div></div>;
  }

  // should never be here..
  return <h1>wierd state !</h1>;
}
